// Which queued photos the page may hand to the native transfer queue (BL-2683, ADR 070 — Ingest Protocol § 7.6).
// Pure, so every reason a photo stays on the foreground executor is testable without a device.
import type { MediaAdd, LotMedia } from './lot-state-media';

/** The native lane that uploads a lot's photos (the name `native-transfer/lanes.ts` freezes). */
const MEDIA_UPLOAD_LANE_NAME = 'media-upload';

/** Input to {@link planMediaHandoff}. */
export interface MediaHandoffInput {
  media: LotMedia;
  /** The lanes the native side lists. */
  lanes: readonly string[];
  /** Staged file ids of photos whose pick-time thumbnail is staged beside them. */
  thumbnailsStaged: ReadonlySet<string>;
}

/**
 * Split a lot's queued adds into the ones native takes and the ones the foreground executor keeps. Native takes
 * a staged photo with a staged thumbnail that is not already native's. Videos, photos already in storage and
 * photos with no staged thumbnail stay (videos wait for BL-2684).
 *
 * @param input - the lot's media, the native lanes and the staged thumbnails.
 * @returns the adds to hand off and the rest.
 */
export function planMediaHandoff(input: MediaHandoffInput): { handoff: MediaAdd[]; foreground: MediaAdd[] } {
  const laneListed = input.lanes.includes(MEDIA_UPLOAD_LANE_NAME);
  const handoff: MediaAdd[] = [];
  const foreground: MediaAdd[] = [];
  for (const add of input.media.adds) {
    const eligible =
      laneListed &&
      !add.native &&
      add.type === 'photo' &&
      add.stagedFileId != null &&
      add.storagePath == null &&
      input.thumbnailsStaged.has(add.stagedFileId);
    (eligible ? handoff : foreground).push(add);
  }
  return { handoff, foreground };
}
